/**
 * Review — where the AI proposes and a human disposes.
 *
 * The screen that decides the product. Every rule it shows comes from
 * `applyGate`, which is tested: what may be accepted, what is blocked and why,
 * and how the acceptance rate is counted.
 *
 * Reject is as prominent as Accept, deliberately. The acceptance rate is only
 * meaningful if rejecting costs the same as accepting — bury the reject button
 * and the number flatters itself.
 */

import { useMemo, useState } from 'react';
import type { CannotAssess, Proposal, Verdict } from '../ai/review.js';
import { useSession } from '../app/SessionProvider.js';
import { DamageMap, mapZones } from '../components/DamageMap.js';
import { extraShotId } from '../capture/shotlist.js';

interface Props {
  proposals: Proposal[];
  verdicts: Record<string, Verdict>;
  onVerdict: (key: string, verdict: Verdict) => void;
  rate: { accepted: number; rejected: number; offered: number; rate: number | null };
  locked: boolean;
  /** What the model could not judge. Often where the money hides. */
  cannotAssess?: CannotAssess[];

  /**
   * Go and take the photograph the model asked for.
   *
   * Reported from the live site: "Add inspect line" put the blind spot on the
   * estimate but there was nowhere to photograph it — the app asked a question
   * it gave you no way to answer. Capture now carries a card per unseen part;
   * this is the way through to it.
   */
  onPhotograph?: () => void;
  /**
   * Shot slots that now hold a photograph.
   *
   * A blind spot the estimator has since photographed is answered, so it drops
   * off this screen — the next read will judge it properly. Leaving it visible
   * turned the list into a pile of things already dealt with, which is how a
   * list stops being read at all.
   */
  takenSlots?: string[];
  /** A decision that did not reach the database. Never hidden. */
  saveError?: string | null;
  /** Overrule the model on repair versus replace. */
  onOperation?: (key: string, operation: 'repair' | 'replace') => void;
  /**
   * Slot -> photograph URL, so evidence can be SEEN.
   *
   * Naming the slot (`HOOD_H`) told an estimator which card it came from, not
   * what the model was looking at. Judging a proposal means looking at the
   * picture behind it.
   */
  photoUrls?: Record<string, string>;
}

const keyOf = (p: Proposal) => `${p.zone}::${p.partLabel}`;

function band(confidence: number): 'hi' | 'md' | 'lo' {
  return confidence >= 80 ? 'hi' : confidence >= 60 ? 'md' : 'lo';
}

type Filter = 'all' | 'pending' | 'accepted' | 'rejected';

/**
 * Remove is not Reject, and the difference is money.
 *
 * Reject asserts the model was wrong, and every rejection lands in the
 * acceptance rate on the dashboard — the one number this product lives or dies
 * on. Remove asserts only that the line does not belong on THIS estimate: a
 * duplicate, something another line already covers, a part the customer
 * declined. Making an estimator reject a correct suggestion in order to tidy
 * their list would quietly corrupt the measurement.
 */
/**
 * Undecided has two spellings and both must count.
 *
 * A proposal nobody has touched has no verdict at all; one that was decided and
 * then undone — or removed and put back — is written `pending`. Testing only for
 * absence made the second kind vanish from "Awaiting you" while still counting
 * in "All", so the tabs stopped summing and a restored proposal could be missed.
 */
const undecided = (v?: Verdict) => !v || v === 'pending';

const REMOVE_HINT =
  'Take it off this estimate. Not counted as the model being wrong — use Reject for that.';

export function Review({ proposals, verdicts, onVerdict, rate, locked,
                        cannotAssess, onPhotograph, photoUrls,
                        takenSlots, saveError, onOperation }: Props) {
  const { t } = useSession();
  const [openEvidence, setOpenEvidence] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState<Filter>('all');

  // One entry per area, carrying the strongest verdict standing there.
  const zones = useMemo(() => mapZones(proposals.map((p) => ({
    zone: p.zone,
    partLabel: p.partLabel,
    confidence: p.confidence,
    verdict: verdicts[`${p.zone}::${p.partLabel}`] ?? 'pending',
  }))), [proposals, verdicts]);

  const decide = (p: Proposal, v: Verdict) => onVerdict(keyOf(p), v);

  const pending = proposals.filter((p) => !p.blocked && undecided(verdicts[keyOf(p)])).length;
  const accepted = proposals.filter((p) => verdicts[keyOf(p)] === 'accepted').length;
  const rejected = proposals.filter((p) => verdicts[keyOf(p)] === 'rejected').length;

  // Removed proposals leave the list entirely. They are NOT rejected: rejecting
  // says the model was wrong and counts against its acceptance rate, which is
  // the number this product is judged on. Removing says "this is noise" — a
  // duplicate, or something already covered by another line — and that is not a
  // verdict on whether the model could see.
  const removed = proposals.filter((p) => verdicts[keyOf(p)] === 'dismissed');
  const live = proposals.filter((p) => verdicts[keyOf(p)] !== 'dismissed');

  const shown = live.filter((p) => {
    if (filter === 'all') return true;
    const v = verdicts[keyOf(p)];
    return filter === 'pending' ? undecided(v) : v === filter;
  });

  // Blind spots the estimator has since photographed are answered: the photo
  // is on the job, the next read will judge it, and the reminder has done its
  // work. Keeping it would leave a list of things already dealt with.
  const taken = new Set(takenSlots ?? []);
  const openBlind = (cannotAssess ?? []).filter((c) => !taken.has(extraShotId(c.partLabel)));
  const answered = (cannotAssess?.length ?? 0) - openBlind.length;

  return (
    <section className="screen">
      <h2>{t('Review')}</h2>
      <p className="muted lede">
        Nothing reaches the estimate until you accept it. Open <b>Why this?</b> to see
        which photographs drove each suggestion.
      </p>

      <div className="reviewbar">
        <span>{pending ? `${pending} still to decide` : 'All decided'}</span>
        <span className="muted">
          {rate.rate === null
            ? 'Acceptance rate — nothing decided yet'
            : `Acceptance ${rate.rate}% · ${rate.accepted} kept, ${rate.rejected} rejected`}
        </span>
      </div>

      {saveError && (
        <div className="note warn">
          <b>That decision was not saved</b>
          {saveError} — it is still shown here, but it will be gone when you come
          back. Check your connection and decide it again.
        </div>
      )}

      {/* Only worth showing when there is something to filter. On a
          three-proposal job the control is noise. */}
      {proposals.length > 3 && (
        <div className="revfilter" role="group" aria-label="Filter proposals">
          {([
            ['all', 'All', live.length],
            ['pending', 'Awaiting you', pending],
            ['accepted', 'Accepted', accepted],
            ['rejected', 'Rejected', rejected],
          ] as Array<[Filter, string, number]>).map(([id, label, n]) => (
            <button key={id} type="button" className={filter === id ? 'on' : ''}
                    aria-pressed={filter === id}
                    disabled={n === 0 && id !== 'all'}
                    onClick={() => setFilter(id)}>
              {label} <span className="n">{n}</span>
            </button>
          ))}
        </div>
      )}

      {/* The map beside the list, not instead of it. The drawing answers
          "how bad and where"; only the list can answer "which part". */}
      <div className="reviewsplit">
        {zones.length > 0 && <DamageMap zones={zones} />}

      <div className="proposals">
        {shown.length === 0 && (
          <p className="muted small">
            Nothing here under this filter. <button className="linkish"
              onClick={() => setFilter('all')}>Show all {proposals.length}</button>
          </p>
        )}
        {shown.map((p) => {
          const k = keyOf(p);
          const v = verdicts[k];
          const decided = v === 'accepted' || v === 'rejected';
          return (
            <article key={k} className={`prop ${decided ? 'done' : ''}`}>
              <header>
                <div className="what">
                  <b>{p.partLabel}</b>
                  <div className="tags">
                    <span className={`tag ${p.workKind}`}>
                      {p.workKind === 'mechanical' ? 'MECHANICAL' : 'BODY'}
                    </span>
                    {/* Repair or replace is the estimator's call, not the
                        model's — it turns on depth, substrate, access and this
                        shop's rate, none of which are in a photograph. The
                        prompt tells the model exactly that; this is the human
                        acting on it. */}
                    {onOperation && !locked
                      && (p.operation === 'replace' || p.operation === 'repair') ? (
                      <span className="optoggle" role="group" aria-label="Repair or replace">
                        {(['repair', 'replace'] as const).map((op) => (
                          <button key={op} type="button"
                                  className={p.operation === op ? 'on' : ''}
                                  aria-pressed={p.operation === op}
                                  onClick={() => onOperation(keyOf(p), op)}>
                            {op}
                          </button>
                        ))}
                      </span>
                    ) : (
                      <span className="tag">{p.operation.toUpperCase()}</span>
                    )}
                    {p.proposedOperation && p.proposedOperation !== p.operation && (
                      <span className="overruled" title="You changed this">
                        model said {p.proposedOperation}
                      </span>
                    )}
                    {p.damageKind && <span className="muted small">{p.damageKind}</span>}
                  </div>
                </div>
                <div className={`conf ${band(p.confidence)}`}>
                  <div className="pc">{p.confidence}%</div>
                  <div className="bar"><i style={{ width: `${p.confidence}%` }} /></div>
                  <div className="cap">confidence</div>
                </div>
              </header>

              <div className="actions">
                {p.blocked ? (
                  <>
                    <button type="button" disabled>Accept</button>
                    <button type="button" className="ghost" title={REMOVE_HINT}
                            onClick={() => decide(p, 'dismissed')}>Remove</button>
                    <span className="blocked">▲ {p.asksFor}</span>
                  </>
                ) : decided ? (
                  <>
                    <span className={`verdict ${v === 'accepted' ? 'yes' : 'no'}`}>
                      {v === 'accepted' ? '✓ Accepted' : '✕ Rejected'}
                    </span>
                    <button type="button" disabled={locked}
                            onClick={() => decide(p, 'pending')}>Undo</button>
                    <button type="button" className="ghost" disabled={locked}
                            title={REMOVE_HINT}
                            onClick={() => decide(p, 'dismissed')}>Remove</button>
                  </>
                ) : (
                  <>
                    <button type="button" className="primary" disabled={locked}
                            onClick={() => decide(p, 'accepted')}>Accept</button>
                    <button type="button" disabled={locked}
                            onClick={() => decide(p, 'rejected')}>Reject</button>
                    {/* Not the same as Reject, and the tooltip says why.
                        Reject means the model was wrong and counts against its
                        acceptance rate; Remove means this is noise — a
                        duplicate, or already covered by another line — which
                        is not a judgement on whether it could see. */}
                    <button type="button" className="ghost" disabled={locked}
                            title={REMOVE_HINT}
                            onClick={() => decide(p, 'dismissed')}>Remove</button>
                  </>
                )}
                <button type="button" className="ghost"
                        onClick={() => setOpenEvidence((o) => ({ ...o, [k]: !o[k] }))}>
                  {openEvidence[k] ? 'Hide' : 'Why this?'}
                </button>
              </div>

              {openEvidence[k] && (
                <div className="evidence">
                  <h4>Photographs behind it</h4>
                  {p.evidence.length === 0 ? (
                    <div className="chips">
                      <span className="none">NONE — inferred, not observed</span>
                    </div>
                  ) : (
                    <div className="evphotos">
                      {p.evidence.map((e) => {
                        const url = photoUrls?.[e];
                        return (
                          <figure key={e} className="evphoto">
                            {/* Falls back to the slot name when there is no
                                URL — the fixture build has no storage, and a
                                broken image would read as a broken product. */}
                            {url
                              ? <img src={url} alt={`Photograph ${e}`} loading="lazy" />
                              : <div className="evnone" aria-hidden="true">no image</div>}
                            <figcaption className="mono">{e}</figcaption>
                          </figure>
                        );
                      })}
                    </div>
                  )}
                  <h4>What the model reported</h4>
                  <ul>{p.observations.map((o, i) => <li key={i}>{o}</li>)}</ul>
                  {p.rationale && (
                    <>
                      <h4>How the confidence was reached</h4>
                      <p className="calc">{p.rationale}</p>
                    </>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>
      </div>

      {/* Named and reversible. A proposal that vanished with no trace reads as
          data loss, and an estimator who removed the wrong one needs a way back. */}
      {removed.length > 0 && !locked && (
        <p className="muted small removednote">
          <b>{removed.length} removed</b> from this estimate —{' '}
          {removed.map((p) => p.partLabel).join(', ')}. Removing is not the same
          as rejecting: it does not count against the AI’s acceptance rate.{' '}
          {/* Put back returns them undecided rather than to whatever they were.
              Deliberate: silently restoring an "accepted" would put money back
              on the estimate without anyone looking at it again. */}
          <button className="linkish" onClick={() => removed.forEach(
            (p) => onVerdict(keyOf(p), 'pending'))}>
            Put {removed.length === 1 ? 'it' : 'them'} back to decide again
          </button>
        </p>
      )}

      {openBlind.length > 0 && (
        <section className="blind">
          <h3>
            It could not see {openBlind.length} thing
            {openBlind.length === 1 ? '' : 's'}
          </h3>
          <p className="muted small">
            Not low confidence — no view of it at all. Some of these no photograph can
            settle, because the part sits behind another one. Those are the ones that
            turn up at teardown.
          </p>
          <ul className="blindlist">
            {openBlind.map((c) => (
              <li key={c.partLabel}>
                <div className="grow">
                  <b>{c.partLabel}</b>
                  <div className="muted small">{c.reason}</div>
                  <div className="want">Wants: {c.photographWanted}</div>
                </div>
                {/* One action, and it is the one that can actually settle the
                    question. An inspect line only booked time to find out
                    later; the card in Capture removes the blind spot now. */}
                {!locked && onPhotograph && (
                  <button className="btn small primary" onClick={onPhotograph}>
                    Photograph it
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Said, not silently dropped: an item that vanished without a word
          reads as a bug, and the estimator needs to know the next read is
          what turns the photograph into a proposal. */}
      {answered > 0 && (
        <p className="muted small blindanswered">
          {answered === 1 ? '1 blind spot has' : `${answered} blind spots have`} been
          photographed and dropped off this list. Run the read again on{' '}
          <b>Capture</b> to have {answered === 1 ? 'it' : 'them'} judged.
        </p>
      )}

    </section>
  );
}
